import { useOnboarding, PRICING } from "./OnboardingContext";
import { CheckCircle, Calendar, Mail, Phone, ArrowRight, Droplets, TrendingUp, Settings, Clock, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

const StepConfirmation = () => {
  const {
    selectedPackage,
    selectedDistributionTier,
    numberOfMachines,
    selectedAddOns,
    monthlyPrice,
    setupFee,
    billingInfo,
    resetOnboarding,
    getPackageDetails,
    getDistributionTierDetails
  } = useOnboarding();

  const navigate = useNavigate();
  const pkg = getPackageDetails();
  const tier = getDistributionTierDetails();
  const hasAcceleratedDeployment = selectedAddOns.includes("acceleratedDeployment");

  const handleBackToHome = () => {
    resetOnboarding();
    navigate("/");
  };

  const handleAccessDashboard = () => {
    alert("Votre espace client sera disponible dès la mise en service de votre dispositif. Vous recevrez un email avec vos identifiants de connexion.");
  };

  if (!pkg) return null;

  return (
    <div data-testid="step-confirmation" className="animate-fade-in max-w-3xl mx-auto">
      {/* Success Header */}
      <div className="text-center py-12 mb-8">
        <div className="w-24 h-24 mx-auto mb-8 bg-green-50 flex items-center justify-center rounded-full">
          <CheckCircle size={48} className="text-green-600" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Précommande enregistrée
        </h1>
        <p className="font-body text-lg text-[#525252] max-w-lg mx-auto">
          Votre projet entre désormais en phase de préparation et de planification.
        </p>
      </div>

      {/* Pre-order Status Banner */}
      <div className="bg-[#0A0A0A] text-white p-8 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#B89F7C]/20 flex items-center justify-center shrink-0">
            <Clock size={24} className="text-[#B89F7C]" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-heading text-xl">Statut de votre projet</h3>
              <span className="bg-[#B89F7C] text-[#0A0A0A] px-3 py-1 text-xs font-medium tracking-wider uppercase">
                En préparation
              </span>
            </div>
            <p className="font-body text-white/80 mb-4">
              <span className="text-[#B89F7C] font-medium">Délai estimé de première mise en service :</span> entre 1 et 4 mois
            </p>
            {hasAcceleratedDeployment && (
              <p className="font-body text-sm text-white/60 flex items-center gap-2">
                <CheckCircle size={14} className="text-[#B89F7C]" />
                Déploiement accéléré activé — Votre projet bénéficie d'une priorité dans le planning.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div className="bg-white border border-[#E5E5E5] p-8 mb-8">
        <h3 className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-6">
          Récapitulatif de votre précommande
        </h3>

        <div className="grid sm:grid-cols-2 gap-8 mb-8">
          <div>
            <h4 className="font-heading text-lg text-[#0A0A0A] mb-4 flex items-center gap-2">
              <Droplets size={18} className="text-[#B89F7C]" />
              Votre offre
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Forfait</span>
                <span className="text-[#0A0A0A] font-medium">{pkg.name}</span>
              </div>
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Flacons</span>
                <span className="text-[#0A0A0A] font-medium">{pkg.flacons}</span>
              </div>
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Engagement</span>
                <span className="text-[#0A0A0A] font-medium">{pkg.engagement} mois</span>
              </div>
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Affichage digital</span>
                <span className="text-[#0A0A0A] font-medium">Jusqu'à {pkg.digitalDisplay}%</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg text-[#0A0A0A] mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-[#B89F7C]" />
              Diffusion & Configuration
            </h4>
            <div className="space-y-3">
              {tier && (
                <div className="flex justify-between font-body text-sm">
                  <span className="text-[#525252]">Niveau de diffusion</span>
                  <span className="text-[#0A0A0A] font-medium">{tier.name}</span>
                </div>
              )}
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#525252]">Machines</span>
                <span className="text-[#0A0A0A] font-medium">{numberOfMachines}</span>
              </div>
              {selectedAddOns.length > 0 && (
                <div className="flex justify-between font-body text-sm">
                  <span className="text-[#525252]">Options</span>
                  <span className="text-[#0A0A0A] font-medium">{selectedAddOns.length} activée{selectedAddOns.length > 1 ? "s" : ""}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="pt-6 border-t border-[#E5E5E5]">
          <div className="flex justify-between items-center mb-3">
            <span className="font-body text-[#525252]">Frais de réservation (payé)</span>
            <span className="font-heading text-lg text-[#0A0A0A]">{setupFee}€ <span className="text-sm font-body">HT</span></span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-body text-[#525252]">Abonnement mensuel (à compter de la mise en service)</span>
            <span className="font-heading text-2xl text-[#0A0A0A]">{monthlyPrice}€ <span className="text-sm font-body">HT/mois</span></span>
          </div>
          <p className="font-body text-xs text-[#525252] mt-2">
            Facturé à : {billingInfo.companyName || "Votre entreprise"}
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-[#F9F6F0] p-8 mb-8">
        <h3 className="font-heading text-xl text-[#0A0A0A] mb-6">
          Les prochaines étapes
        </h3>
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0A0A0A] text-white flex items-center justify-center font-body text-sm font-medium shrink-0">
              1
            </div>
            <div>
              <h4 className="font-body font-medium text-[#0A0A0A] mb-1">
                Confirmation par email
              </h4>
              <p className="font-body text-sm text-[#525252]">
                Vous recevrez un email de confirmation avec le récapitulatif de votre précommande et votre contrat signé.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0A0A0A] text-white flex items-center justify-center font-body text-sm font-medium shrink-0">
              2
            </div>
            <div>
              <h4 className="font-body font-medium text-[#0A0A0A] mb-1">
                Prise de contact
              </h4>
              <p className="font-body text-sm text-[#525252]">
                Notre équipe vous contactera pour organiser la préparation de votre projet et recueillir vos fragrances.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0A0A0A] text-white flex items-center justify-center font-body text-sm font-medium shrink-0">
              3
            </div>
            <div>
              <h4 className="font-body font-medium text-[#0A0A0A] mb-1">
                Planification du déploiement
              </h4>
              <p className="font-body text-sm text-[#525252]">
                Nous sélectionnerons les lieux partenaires adaptés à votre niveau de diffusion "{tier?.name}" et planifierons la mise en service.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#B89F7C] text-white flex items-center justify-center font-body text-sm font-medium shrink-0">
              4
            </div>
            <div>
              <h4 className="font-body font-medium text-[#0A0A0A] mb-1">
                Mise en service
              </h4>
              <p className="font-body text-sm text-[#525252]">
                Activation de votre dispositif au sein de notre réseau partenaire. Votre marque devient visible dans des lieux premium !
              </p>
              <p className="font-body text-xs text-[#B89F7C] mt-1">
                Délai estimé : entre 1 et 4 mois selon disponibilités opérationnelles.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Note */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-8 flex items-start gap-4">
        <Info size={20} className="text-[#B89F7C] shrink-0 mt-1" />
        <div>
          <h4 className="font-body font-medium text-[#0A0A0A] mb-1">
            Information importante
          </h4>
          <p className="font-body text-sm text-[#525252]">
            Le délai de mise en service est estimé entre 1 et 4 mois et dépend des disponibilités opérationnelles, 
            du planning de déploiement et des contraintes du réseau partenaire. Notre équipe vous tiendra informé 
            de l'avancement de votre projet.
          </p>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white border border-[#E5E5E5] p-6 mb-8">
        <h3 className="font-body text-sm tracking-[0.15em] uppercase text-[#525252] mb-4">
          Une question ?
        </h3>
        <div className="flex flex-col sm:flex-row gap-6">
          <a
            href="mailto:hello@lepschittfrancais.fr"
            className="flex items-center gap-3 font-body text-[#0A0A0A] hover:text-[#B89F7C] transition-colors"
          >
            <Mail size={18} strokeWidth={1.5} />
            hello@lepschittfrancais.fr
          </a>
          <a
            href="tel:+33650276753"
            className="flex items-center gap-3 font-body text-[#0A0A0A] hover:text-[#B89F7C] transition-colors"
          >
            <Phone size={18} strokeWidth={1.5} />
            +33 6 50 27 67 53
          </a>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={handleBackToHome}
          data-testid="btn-back-home"
          className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-10 py-4 text-sm tracking-widest uppercase font-medium inline-flex items-center justify-center gap-3"
        >
          Retour à l'accueil
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default StepConfirmation;
